import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  BlockNode,
  InlineNode,
  LinkTarget,
  ListItem,
  parseAssistantMarkdown,
} from "./assistant-markdown";

/**
 * Renders an assistant message.
 *
 * The assistant writes markdown by habit, and the bubble used to print it
 * literally — employees read "**3 ataskaitos**" and a link they could only
 * select and copy. Parsing happens in ./assistant-markdown, which yields data;
 * this file turns that data into React elements.
 *
 * There is no HTML string and no sanitiser anywhere in this path. Every value
 * becomes either a React text node — which React escapes by construction — or
 * one of exactly two URL attributes, each re-checked here immediately before it
 * is written.
 */

const LINK_CLASS =
  "text-indigo-300 underline underline-offset-2 decoration-indigo-400/40 " +
  "hover:text-indigo-200";

/**
 * The destination was already validated during parsing. Checking it again here
 * is deliberate: this is the last line before the value reaches the DOM, and
 * either check alone is sufficient, so deleting one cannot open a hole.
 */
const AssistantLink: React.FC<{ target: LinkTarget; label: string }> = ({
  target,
  label,
}) => {
  if (target.kind === "internal") {
    // A path starting with "//" is a protocol-relative address once react-router
    // forwards it into href, and a click would leave the app entirely.
    if (!/^\/(?!\/)/.test(target.path)) return <>{label}</>;
    return (
      <Link to={target.path} className={`${LINK_CLASS} break-words`}>
        {label}
      </Link>
    );
  }

  if (!/^https?:\/\//i.test(target.href)) return <>{target.href}</>;
  return (
    <a
      href={target.href}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
      className={`${LINK_CLASS} break-all`}
    >
      {/* The address is its own label. A model-supplied label could read
          "https://darbuotojams.uhb.lt/login" while pointing somewhere else. */}
      {target.href}
    </a>
  );
};

const renderInline = (nodes: InlineNode[], keyPrefix: string): React.ReactNode =>
  nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (node.type) {
      case "break":
        return <br key={key} />;
      case "strong":
        return (
          <strong key={key} className="font-semibold text-white">
            {renderInline(node.children, key)}
          </strong>
        );
      case "em":
        return (
          <em key={key} className="italic">
            {renderInline(node.children, key)}
          </em>
        );
      case "code":
        // Deliberately drab: a code span in an answer means the assistant leaked
        // something internal, so it should read as a mistake, not a feature.
        return (
          <code
            key={key}
            className="rounded bg-gray-900/70 px-1 py-px font-mono text-[0.85em] text-gray-300"
          >
            {node.value}
          </code>
        );
      case "link":
        return <AssistantLink key={key} target={node.target} label={node.label} />;
      default:
        return <React.Fragment key={key}>{node.value}</React.Fragment>;
    }
  });

const renderItems = (
  items: ListItem[],
  ordered: boolean,
  start: number,
  keyPrefix: string,
  nested: boolean
): React.ReactNode => {
  const Tag = ordered ? "ol" : "ul";
  const marker = ordered
    ? "list-decimal pl-5 marker:tabular-nums"
    : "list-disc pl-4";
  const spacing = nested ? "pl-3 my-0.5" : "my-1.5 first:mt-0";
  return (
    <Tag
      // A blank line between steps closes the list, so the model's own "2."
      // must be carried across or every step would render as "1.".
      start={ordered ? start : undefined}
      className={`${marker} ${spacing} space-y-0.5 marker:text-gray-500`}
    >
      {items.map((item, index) => {
        const key = `${keyPrefix}-${index}`;
        return (
          <li key={key} className="leading-snug break-words">
            {renderInline(item.children, key)}
            {item.nested
              ? renderItems(
                item.nested.items,
                item.nested.ordered,
                item.nested.start,
                `${key}-n`,
                true
              )
              : null}
          </li>
        );
      })}
    </Tag>
  );
};

const renderBlock = (block: BlockNode, key: string): React.ReactNode => {
  switch (block.type) {
    case "heading":
      // Every level renders the same, at body size. A real heading scale would
      // put 24px type above a one-sentence answer in a 261px column, and the
      // literal "##" looks broken.
      return (
        <p key={key} className="mt-2 first:mt-0 font-semibold text-white">
          {renderInline(block.children, key)}
        </p>
      );
    case "list":
      return (
        <React.Fragment key={key}>
          {renderItems(block.items, block.ordered, block.start, key, false)}
        </React.Fragment>
      );
    default:
      return (
        <p key={key} className="[&:not(:first-child)]:mt-2 leading-snug break-words">
          {renderInline(block.children, key)}
        </p>
      );
  }
};

export const MessageText: React.FC<{ content: string; streaming?: boolean }> = ({
  content,
  streaming,
}) => {
  // Content grows one delta at a time and the bubble re-renders on each, so
  // without this the whole answer is re-parsed on every frame.
  const blocks = useMemo(
    () => parseAssistantMarkdown(content, window.location.origin, { streaming }),
    [content, streaming]
  );
  return <>{blocks.map((block, index) => renderBlock(block, `b-${index}`))}</>;
};

export default MessageText;
